import type { DecoBuildCtx } from "./define.ts";
import { shade, rand } from "../color.ts";

// A wooden treasure chest — placed prop (weight 0, content/world/props.json),
// openable via its `interact` block. Two builds share the body: the default
// (closed) and `chestOpen` (lid tipped back, gold glinting inside) — the client
// swaps to the open build when the server marks the chest opened. Geometry only —
// metadata lives in content/decos/chest.json; both builds register in
// src/model/coded.ts ("chest" / "chest-open").

const W = 1.1;   // body width (x)
const D = 0.7;   // body depth (z)
const BODY_H = 0.5;
const LID_H = 0.22;

function body({ box, seed }: DecoBuildCtx): { wood: number; iron: number } {
  const wood = shade(0x7a5228, (rand(seed) - 0.5) * 0.16);
  const iron = shade(0x4c4a52, (rand(seed + 1) - 0.5) * 0.1);
  box(0, BODY_H / 2, 0, W, BODY_H, D, wood);
  // Iron strapping: two vertical bands + corner feet.
  for (const sx of [-W * 0.32, W * 0.32]) {
    box(sx, BODY_H / 2, 0, 0.09, BODY_H + 0.02, D + 0.04, iron);
  }
  for (const sx of [-W / 2 + 0.07, W / 2 - 0.07]) {
    for (const sz of [-D / 2 + 0.07, D / 2 - 0.07]) {
      box(sx, 0.045, sz, 0.12, 0.09, 0.12, shade(iron, -0.15));
    }
  }
  return { wood, iron };
}

/** Closed chest (the default "chest" model). */
const build: (ctx: DecoBuildCtx) => void = (ctx) => {
  const { box } = ctx;
  const { wood, iron } = body(ctx);
  // Domed lid: stacked, narrowing slabs read as a curve under toon shading.
  box(0, BODY_H + LID_H * 0.35, 0, W, LID_H * 0.7, D, shade(wood, 0.08));
  box(0, BODY_H + LID_H * 0.8, 0, W * 0.96, LID_H * 0.5, D * 0.72, shade(wood, 0.14));
  for (const sx of [-W * 0.32, W * 0.32]) {
    box(sx, BODY_H + LID_H * 0.5, 0, 0.09, LID_H, D + 0.04, iron);
  }
  // Clasp on the front face.
  box(0, BODY_H - 0.04, D / 2 + 0.02, 0.16, 0.2, 0.06, shade(iron, 0.12));
  box(0, BODY_H - 0.1, D / 2 + 0.045, 0.07, 0.09, 0.03, 0xd9b65a); // gold keyhole plate
};

/** Opened chest (the "chest-open" model): lid tipped back, treasure showing. */
export const chestOpen: (ctx: DecoBuildCtx) => void = (ctx) => {
  const { box, blob, seed } = ctx;
  const { wood, iron } = body(ctx);
  // Lid leaning back off the rear edge (a static tipped slab — no hinge rig).
  box(0, BODY_H + 0.26, -D / 2 - 0.16, W, D, LID_H * 0.8, shade(wood, 0.08));
  for (const sx of [-W * 0.32, W * 0.32]) {
    box(sx, BODY_H + 0.26, -D / 2 - 0.18, 0.09, D + 0.04, 0.1, iron);
  }
  // A mound of gold spilling over the rim.
  const gold = 0xd9b65a;
  blob(0, BODY_H - 0.05, 0, 0.3, gold);
  blob(-0.25, BODY_H - 0.1, 0.08, 0.18, shade(gold, 0.12));
  blob(0.22, BODY_H - 0.08, -0.06, 0.16, shade(gold, -0.08));
  blob(0.1 + rand(seed + 2) * 0.1, BODY_H + 0.02, 0.1, 0.1, shade(gold, 0.2));
};

export default build;
